import { TextField } from '@tunasong/ui-lib'
import type { PastCollaboratorEntry } from '@tunasong/graph-lib'
import { graphHooks } from '@tunasong/graph-lib/react'
import { getLink, hasPublicAccess, isEmail } from '@tunasong/models'
import { profilesApi, useCurrentUser } from '@tunasong/redux'
import type { ACL, Entity, ItemOperation, Persisted, Profile } from '@tunasong/schemas'
import { isProfile } from '@tunasong/schemas'
import {
  ConfirmDialog,
  CopyToClipboard,
  Dialog,
  ShareList,
  Slug,
  TransferEntity,
  getIFrameUrl,
  Button,
  CollaboratorRow,
  LinearProgress,
  Tab,
  Tabs,
  FormControlLabel,
  Checkbox,
  Typography,
} from '@tunasong/ui-lib'
import type { ChangeEvent, FC, KeyboardEvent } from 'react'
import React, { useCallback, useEffect, useState } from 'react'
import styles from './share-dialog.module.css'

export interface ShareInfoProps {
  className?: string
  open: boolean
  entity: Persisted<Entity>
  title?: string
  isLoading?: boolean
  onShare(acls: ACL[]): void
  onInvite(email: string, allow: ItemOperation): void
  onClose(): void
}

export const ShareDialog: FC<ShareInfoProps> = props => {
  const { className, entity: item, onShare, onInvite, onClose, isLoading, open, title = 'Share' } = props
  const [email, setEmail] = useState('')
  const { profiles } = graphHooks.useProfiles()
  const { userId } = useCurrentUser()

  const { entity: parentEntity } = graphHooks.useEntity<Entity>(item.parentId)

  const validEmail = isEmail(email)
  const [showInvite, setShowInvite] = useState(false)
  const [pending, setPending] = useState<string[]>([])

  /** Past-collaborator suggestions for the share input. */
  const pastCollaborators = graphHooks.usePastCollaborators({
    excludePrincipals: [userId, ...(item.acls ?? []).map(a => a.principal)].filter((s): s is string => Boolean(s)),
  })
  const matchesQuery = (entry: PastCollaboratorEntry, q: string): boolean => {
    if (!q) {
      return true
    }
    const lq = q.toLowerCase()
    const nameMatch = entry.profile.name?.toLowerCase().includes(lq) ?? false
    return entry.profile.email.toLowerCase().includes(lq) || nameMatch
  }
  const suggestions = pastCollaborators.filter(e => matchesQuery(e, email)).slice(0, 7)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)

  const handleInvite = () => {
    setEmail('')
    setShowInvite(false)
    if (onInvite) {
      onInvite(email, 'WRITE')
      setPending(pending => [...pending, email])
    }
  }
  const handleDeclineInvite = () => {
    setEmail('')
    setShowInvite(false)
  }

  const [loadProfile, { isLoading: isLoadingProfile }] = profilesApi.useLazyLoadProfileQuery()
  const addShare = async () => {
    /** Check if the user/profile exists */
    let profile: Profile | undefined = Object.values(profiles ?? []).filter(p => p.email === email)[0]
    if (!isProfile(profile)) {
      profile = await loadProfile({ emailOrUserId: email }, true)
        .unwrap()
        .catch(() => undefined)
    }

    if (!profile) {
      setShowInvite(true)
      return
    }

    const principal = profile.userId

    /** Push the ACL onto the ACL list */
    onShare([
      ...(item.acls || []),
      {
        principal,
        permission: 'WRITE',
      },
    ])

    /** Clear input */
    setEmail('')
  }

  const handlePick = (profile: Profile) => {
    onShare([
      ...(item.acls ?? []),
      {
        principal: profile.userId,
        permission: 'WRITE',
      },
    ])
    setEmail('')
    setHighlightedIndex(-1)
  }

  const handleClose = async () => {
    if (onClose) {
      onClose()
    }
  }

  const handleReturn = (ev: KeyboardEvent) => {
    if (ev.key === 'ArrowDown' && suggestions.length > 0) {
      ev.preventDefault()
      setHighlightedIndex(prev => (prev + 1 + suggestions.length) % suggestions.length)
      return
    }
    if (ev.key === 'ArrowUp' && suggestions.length > 0) {
      ev.preventDefault()
      setHighlightedIndex(prev => (prev - 1 + suggestions.length) % suggestions.length)
      return
    }
    if (ev.key === 'Escape' && suggestions.length > 0) {
      ev.preventDefault()
      setHighlightedIndex(-1)
      return
    }
    if (ev.key === 'Enter' && highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
      ev.preventDefault()
      handlePick(suggestions[highlightedIndex].profile)
      return
    }
    if (ev.key !== 'Enter' || !validEmail) {
      return
    }
    void addShare()
  }
  const handleDelete = (acl: ACL) => {
    const updatedAcls = [...(item.acls ?? [])]
    updatedAcls.splice(updatedAcls.indexOf(acl), 1)
    onShare(updatedAcls)
  }

  const handleEmail = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEmail(ev.target.value)
  }

  const handlePublicAccess = (ev: ChangeEvent<HTMLInputElement>) => {
    if (!ev.target.checked) {
      const acl = item.acls?.find(acl => acl.principal === 'PUBLIC')
      if (!acl) {
        throw new Error(`Cannot find public ACL!`)
      }
      return handleDelete(acl)
    }
    onShare([
      ...(item.acls || []),
      {
        principal: 'PUBLIC',
        permission: 'READ',
      },
    ])
  }

  /** Share tab */
  const [shareTab, setShareTab] = useState(0)
  const handleShareTab = useCallback(
    (_ev: React.SyntheticEvent | null, val: string | number) => setShareTab(val as number),
    []
  )

  const isOwner = item.userId === userId

  /** We can't show the share tab when we're not the owner */
  useEffect(() => {
    if (!isOwner && shareTab === 3) {
      setShareTab(0)
    }
  }, [isOwner, shareTab])

  const handleCloneParent = () => {
    if (!item.parentId) {
      return
    }
    const parentAcls = parentEntity?.acls ?? []
    onShare(parentAcls)
  }

  const entityUrl = URL.createObjectURL(new Blob([JSON.stringify(item)], { type: 'application/json' }))

  if (!item) {
    return null
  }

  const inviteText = `No user found for ${email}. Do you want to invite ${email} to Tunasong? The ${item.type} will be available to them when they sign up.`
  const url = getLink(item)

  const isPublic = hasPublicAccess(item.acls)
  const iFrameCode = getIFrameUrl(item)
  const handleTransferred = () => {
    setShareTab(0)
  }

  const isBusy = isLoading || isLoadingProfile

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        title={title}
        showClose={false}
        fullWidth={true}
        maxWidth={'sm'}
        onClick={ev => ev.stopPropagation()}
        className={className}
      >
        {/* Share tools */}
        <Tabs className={styles.tabs} value={shareTab} onChange={handleShareTab}>
          <Tab label={`Users`} value={0} />
          <Tab label={`Link`} value={1} />
          <Tab label={`Embed`} value={2} />
          <Tab label={`Export`} value={3} />
          {isOwner ? <Tab label={`Transfer Ownership`} value={4} /> : null}
        </Tabs>
        {isBusy ? <LinearProgress /> : null}
        {shareTab === 0 && (
          <>
            <div className={styles.shareRow}>
              <TextField
                disabled={isBusy}
                className={styles.shareInput}
                key={item.id}
                placeholder="E-mail to share with"
                autoFocus={true}
                variant="outlined"
                onKeyDown={handleReturn}
                onChange={handleEmail}
              />

              <Button disabled={!validEmail || isBusy} onClick={addShare}>
                Add
              </Button>
            </div>
            {suggestions.length > 0 && (
              <div className={styles.suggestionList} role="listbox" aria-label="Past collaborators">
                {suggestions.map((entry, i) => (
                  <CollaboratorRow
                    key={entry.profile.userId}
                    profile={entry.profile}
                    highlighted={i === highlightedIndex}
                    onClick={() => handlePick(entry.profile)}
                  />
                ))}
              </div>
            )}
            <ShareList entity={item} onDelete={handleDelete} pending={pending} />
            <Button disabled={!item.parentId || isBusy} onClick={handleCloneParent}>
              Copy collaborators from parent
            </Button>
          </>
        )}
        {shareTab === 1 && (
          <div className={styles.linkRow}>
            <Typography variant="body2">{url}</Typography>
            <CopyToClipboard variant="icon" text={url} title="Copy URL" />
          </div>
        )}

        {shareTab === 2 && (
          <div className={styles.embedSection}>
            <FormControlLabel
              control={<Checkbox checked={isPublic} onChange={handlePublicAccess} name="public-access" />}
              label="Enable public read access"
            />
            {isPublic && (
              <>
                <Typography variant="h5" gutterBottom>
                  Slug
                </Typography>
                <Slug entityId={item.id} />

                <Typography variant="h5" gutterBottom>
                  Embed IFrame
                </Typography>
                <div className={styles.embedCodeRow}>
                  <code>{iFrameCode}</code>
                  <CopyToClipboard variant="icon" text={iFrameCode} title="Copy Embed code" />
                </div>
              </>
            )}
          </div>
        )}
        {shareTab === 3 ? (
          <>
            <Button
              href={entityUrl}
              target="_blank"
              disabled={!entityUrl}
              download={item.name ? `${item.name}.json` : `${item.id}.json`}
              component={'a'}
              fullWidth
            >
              Export entity as JSON
            </Button>
          </>
        ) : null}
        {shareTab === 4 ? <TransferEntity entity={item} onTransferred={handleTransferred} /> : null}
      </Dialog>

      <ConfirmDialog
        open={showInvite}
        onConfirm={handleInvite}
        onCancel={handleDeclineInvite}
        text={inviteText}
      ></ConfirmDialog>
    </>
  )
}

export default ShareDialog
